import React from 'react'
import { useTranslation } from 'react-i18next'
import { Check, MessageCircle } from 'lucide-react'

const Pricing = () => {
  const { t } = useTranslation()
  return (
    <section id="pricing" className="py-20 sm:py-24 md:py-32 px-4 sm:px-6 bg-[#0a0a0f] relative">
      <div className="absolute inset-0">
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container-custom relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-16">
          <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">{t('pricing.badge', 'Pricing')}</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-3 sm:mt-4 mb-4 sm:mb-6 text-white">
            {t('pricing.heading1', 'Transparent pricing.')}<br />
            <span className="text-gray-400">{t('pricing.heading2', 'No surprises.')}</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-400">
            {t('pricing.description', "Every project is unique. Let's discuss your needs and give you a custom quote.")}
          </p>
        </div>

        {/* Custom Quote Card */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] border-2 border-purple-500/30 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-12 text-center">
            <div className="w-20 h-20 bg-purple-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <MessageCircle className="w-10 h-10 text-purple-400" />
            </div>
            
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">
              {t('pricing.cardHeading', 'Custom Quote')}
            </h3>
            
            <p className="text-gray-400 mb-6 sm:mb-8 text-base sm:text-lg">
              {t('pricing.cardDesc', "We don't believe in pre-made packages. Every business is different, and we design solutions based on your specific needs and budget.")}
            </p>

            <div className="space-y-4 mb-8 text-left">
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-purple-400 flex-shrink-0" />
                <span className="text-gray-300">{t('pricing.check1', 'Free consultation call')}</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-purple-400 flex-shrink-0" />
                <span className="text-gray-300">{t('pricing.check2', 'Clear, fixed quote')}</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-purple-400 flex-shrink-0" />
                <span className="text-gray-300">{t('pricing.check3', 'No hidden fees')}</span>
              </div>
              <div className="flex items-center gap-3">
                <Check className="w-5 h-5 text-purple-400 flex-shrink-0" />
                <span className="text-gray-300">{t('pricing.check4', 'Ongoing support available')}</span>
              </div>
            </div>

            <a 
              href="https://cal.com/mostafa-elsheikh/30min" 
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-purple-600 text-white font-semibold rounded-xl transition-all duration-200 hover:scale-105 active:scale-[0.98] hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.5)] text-lg"
            >
              {t('pricing.getQuote', 'Get a Free Quote')}
            </a>

            <p className="mt-6 text-gray-500 text-sm">
              {t('pricing.note', 'Note: n8n projects start at $200 and exceed $5000 depending on complexity')}
            </p>
          </div>
        </div>

        {/* Monthly Retainer Note */}
        <div className="mt-12 bg-gradient-to-r from-purple-500/10 to-purple-500/10 rounded-2xl p-6 border border-white/10 max-w-2xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left">
            <div>
              <h4 className="text-white font-semibold mb-1">{t('pricing.retainerTitle', 'Need ongoing support?')}</h4>
              <p className="text-gray-400 text-sm">{t('pricing.retainerDesc', 'Monthly maintenance plans available for existing clients')}</p>
            </div>
            <a href="#cta" className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-purple-500/70 text-purple-400 font-semibold rounded-xl hover:bg-purple-500/10 hover:text-purple-300 active:scale-[0.98] transition-all duration-200 text-sm">
              {t('pricing.contactUs', 'Contact Us')}
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Pricing